import PropTypes from 'prop-types';
import { Fab } from '@mui/material';

/**
 * A reusable, animated Floating Action Button that flawlessly expands on hover.
 * This version uses a robust max-width animation to prevent hover-out glitches.
 *
 * @param {object} props The component props.
 * @param {React.ReactNode} props.icon The icon to display in the button.
 * @param {string} props.label The text to reveal on hover.
 */
const ExpandingFab = ({ icon, label, color, onClick, ...props }) => {
	return (
		<Fab
			variant="extended"
			color={color || 'primary'}
			{...props}
			onClick={onClick}
			sx={{
				// --- Base & Default State (Perfectly Circular) ---
				position: 'fixed',
				bottom: 40,
				right: 40,
				height: '32px',
				background: '#0052cc',
				// The container's padding is constant, its width is determined by its content
				pl: '20px', // Horizontal padding to center the icon when collapsed
				pr: '20px',
				transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Animate all properties smoothly

				// --- The Text Label ---
				// MUI wraps the text in a span with this class.
				'& .MuiFab-extended-label': {
					display: 'inline-flex',
					alignItems: 'center',
					// --- Default Hidden State ---
					maxWidth: 0, // Start with no width
					opacity: 0, // Fully transparent
					transition: 'max-width 0.25s ease-out, opacity 0.2s ease-out 0.1s', // Animate both properties
				},

				// --- The Icon ---
				// We target the icon passed as a child
				'& .MuiSvgIcon-root': {
					marginRight: 0, // No margin by default
					transition: 'margin-right 0.25s ease-out',
				},

				// --- Hover State ---
				'&:hover': {
					// On hover, create space for the text next to the icon
					'& .MuiSvgIcon-root': {
						marginRight: 1, // 8px
					},
					// Reveal the text
					'& .MuiFab-extended-label': {
						maxWidth: '200px', // Animate to a width large enough for any label
						opacity: 1,
					},
				},
			}}
		>
			{icon}
			{label}
		</Fab>
	);
};

ExpandingFab.propTypes = {
	icon: PropTypes.node.isRequired,
	label: PropTypes.string.isRequired,
};

export default ExpandingFab;
